import type { CaptureResult } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { App } from "./App.tsx";
import { stripBoundaryEventUrls } from "./components/errorBoundary/reportBoundaryError";
import "./css/globals.css";
import { mountAfterFirstPaint } from "./lib/mountAfterFirstPaint";
import {
  clearPosthogStorage,
  hasAnalyticsConsent,
  isAppPath,
  posthogPersistence,
} from "./lib/analyticsConsent";

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  person_profiles: 'always',
  // Cookie e localStorage de análise só depois do "Aceitar" no banner; até lá,
  // o id do visitante vive só na memória da aba (src/lib/analyticsConsent.ts).
  persistence: posthogPersistence(),
  // SPA: sem isso, o $pageview só dispara no load inicial — navegação via
  // React Router (ex.: entre posts do blog) não gera pageview nenhum.
  capture_pageview: 'history_change',
  // /backoffice (e /backoffice/*) nunca é rastreado.
  before_send: (event: CaptureResult | null): CaptureResult | null => {
    if (typeof window === "undefined") return event;

    const { pathname } = window.location;
    if (pathname.startsWith("/backoffice")) {
      return null;
    }
    // Site público: nada sai sem o aceite no banner de cookies. Vale para todo
    // evento, inclusive pageview, autocapture e gravação de sessão.
    if (!isAppPath(pathname) && !hasAnalyticsConsent()) {
      return null;
    }
    return stripBoundaryEventUrls(event);
  },
} as const;

// Checagem por hostname (não por env.ENVIRONMENT/IS_DEV): o requisito é nunca
// rastrear localhost mesmo que VITE_ENVIRONMENT esteja setado como produção
// nesse ambiente — uma env var mal configurada não pode religar o tracking.
const isLocalhost = ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);

if (!isLocalhost && !hasAnalyticsConsent()) clearPosthogStorage();

const app = isLocalhost ? (
  <App />
) : (
  <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={options}>
    <App />
  </PostHogProvider>
);

// HTML pré-renderizado (home, LPs) ou SSR (blog): monta só depois de o
// navegador registrar a primeira pintura dele (ver mountAfterFirstPaint.ts).
const rootElement = document.getElementById("root")!;
mountAfterFirstPaint(rootElement, () =>
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <HelmetProvider>{app}</HelmetProvider>
    </React.StrictMode>
  )
);
